import { 
  BillEvent, 
  BillEventType, 
  BillCreatedEvent,
  BillChangedEvent,
  BillSentEvent,
  PaymentReceivedEvent,
  PaymentSentEvent,
  FullyPaidEvent,
  generateEventId 
} from '@/types/billEvent';

export const billEventService = {
  /**
   * Create a "Bill Created" event
   */
  createBillCreatedEvent: (
    generatedBy: string,
    generatedByName?: string
  ): BillCreatedEvent => {
    const event: BillCreatedEvent = {
      id: generateEventId(),
      type: BillEventType.BILL_CREATED,
      timestamp: new Date(),
      triggeredBy: generatedBy,
      details: {
        generatedBy
      }
    };

    // Only add optional fields if they have values
    if (generatedByName) {
      event.triggeredByName = generatedByName;
      event.details!.generatedByName = generatedByName;
    }

    return event;
  },

  /**
   * Create a "Bill Changed" event
   */
  createBillChangedEvent: (
    triggeredBy: string,
    triggeredByName?: string,
    changedFields?: string[],
    previousValues?: Record<string, unknown>
  ): BillChangedEvent => {
    const event: BillChangedEvent = {
      id: generateEventId(),
      type: BillEventType.BILL_CHANGED,
      timestamp: new Date(),
      triggeredBy,
      details: {}
    };

    // Only add optional fields if they have values
    if (triggeredByName) {
      event.triggeredByName = triggeredByName;
    }
    if (changedFields) {
      event.details!.changedFields = changedFields;
    }
    if (previousValues) {
      event.details!.previousValues = previousValues;
    }

    return event;
  },

  /**
   * Create a "Bill Sent" event
   */
  createBillSentEvent: (
    triggeredBy: string,
    triggeredByName?: string,
    sentTo?: string[],
    deliveryMethod?: string
  ): BillSentEvent => {
    const event: BillSentEvent = {
      id: generateEventId(),
      type: BillEventType.BILL_SENT,
      timestamp: new Date(),
      triggeredBy,
      details: {}
    };

    // Only add optional fields if they have values
    if (triggeredByName) {
      event.triggeredByName = triggeredByName;
    }
    if (sentTo) {
      event.details!.sentTo = sentTo;
    }
    if (deliveryMethod) {
      event.details!.deliveryMethod = deliveryMethod;
    }

    return event;
  },

  /**
   * Create a "Payment Received" event
   */
  createPaymentReceivedEvent: (
    amount: number,
    totalPaidAmount: number,
    remainingAmount: number,
    triggeredBy?: string,
    triggeredByName?: string,
    paymentMethod?: string,
    reference?: string
  ): PaymentReceivedEvent => {
    const event: PaymentReceivedEvent = {
      id: generateEventId(),
      type: BillEventType.PAYMENT_RECEIVED,
      timestamp: new Date(),
      details: {
        amount,
        totalPaidAmount,
        remainingAmount
      }
    };

    // Only add optional fields if they have values
    if (triggeredBy) {
      event.triggeredBy = triggeredBy;
    }
    if (triggeredByName) {
      event.triggeredByName = triggeredByName;
    }
    if (paymentMethod) {
      event.details!.paymentMethod = paymentMethod;
    }
    if (reference) {
      event.details!.reference = reference;
    }

    return event;
  },

  /**
   * Create a "Payment Sent" event
   */
  createPaymentSentEvent: (
    amount: number,
    triggeredBy?: string,
    triggeredByName?: string,
    recipient?: string,
    paymentMethod?: string,
    reference?: string
  ): PaymentSentEvent => {
    const event: PaymentSentEvent = {
      id: generateEventId(),
      type: BillEventType.PAYMENT_SENT,
      timestamp: new Date(),
      details: {
        amount
      }
    };

    // Only add optional fields if they have values
    if (triggeredBy) {
      event.triggeredBy = triggeredBy;
    }
    if (triggeredByName) {
      event.triggeredByName = triggeredByName;
    }
    if (recipient) {
      event.details!.recipient = recipient;
    }
    if (paymentMethod) {
      event.details!.paymentMethod = paymentMethod;
    }
    if (reference) {
      event.details!.reference = reference;
    }

    return event;
  },

  /**
   * Create a "Fully Paid" event
   */
  createFullyPaidEvent: (
    totalAmount: number,
    triggeredBy?: string,
    triggeredByName?: string,
    finalPaymentAmount?: number
  ): FullyPaidEvent => {
    const event: FullyPaidEvent = {
      id: generateEventId(),
      type: BillEventType.FULLY_PAID,
      timestamp: new Date(),
      details: {
        totalAmount
      }
    };

    // Only add optional fields if they have values
    if (triggeredBy) {
      event.triggeredBy = triggeredBy;
    }
    if (triggeredByName) {
      event.triggeredByName = triggeredByName;
    }
    if (finalPaymentAmount !== undefined) {
      event.details!.finalPaymentAmount = finalPaymentAmount;
    }

    return event;
  },

  /**
   * Sort events chronologically (newest first for timeline display)
   */
  sortEventsByTimestamp: (events: BillEvent[], ascending: boolean = false): BillEvent[] => {
    return [...events].sort((a, b) => {
      const diff = a.timestamp.getTime() - b.timestamp.getTime();
      return ascending ? diff : -diff;
    });
  },

  /**
   * Get the latest event of a specific type
   */
  getLatestEventOfType: (events: BillEvent[], eventType: BillEventType): BillEvent | null => {
    const eventsOfType = events.filter(event => event.type === eventType);
    if (eventsOfType.length === 0) return null;
    
    return eventsOfType.reduce((latest, current) => 
      current.timestamp > latest.timestamp ? current : latest
    );
  },

  /**
   * Check if a specific event type exists in the events array
   */
  hasEventOfType: (events: BillEvent[], eventType: BillEventType): boolean => {
    return events.some(event => event.type === eventType);
  },

  /**
   * Get events within a date range
   */
  getEventsInDateRange: (
    events: BillEvent[], 
    startDate: Date, 
    endDate: Date
  ): BillEvent[] => {
    return events.filter(event => 
      event.timestamp >= startDate && event.timestamp <= endDate
    );
  }
};
